/**
 * API Error Codes - Client-safe enum only
 *
 * This module can be safely imported in browser code without tree-shaking concerns.
 * It contains only the enum definition, no internal messages or status mappings.
 *
 * Naming convention: DOMAIN_DESCRIPTION
 * - DOMAIN: Category (AUTH, VALIDATION, ORG, ADMIN, APPCHECK, etc.)
 * - DESCRIPTION: What went wrong (SESSION_MISSING, INVALID_INPUT, etc.)
 */
export enum ApiErrorCode {
  // Session errors (403 Forbidden)
  AUTH_SESSION_MISSING = 'AUTH_SESSION_MISSING',
  AUTH_SESSION_EXPIRED = 'AUTH_SESSION_EXPIRED',
  AUTH_SESSION_INVALID = 'AUTH_SESSION_INVALID',
  // Token validation (401 Unauthorized)
  AUTH_TOKEN_INVALID = 'AUTH_TOKEN_INVALID',
  // Access denied (403 Forbidden)
  AUTH_ACCESS_DENIED = 'AUTH_ACCESS_DENIED',

  // Admin endpoint authorization (401 Unauthorized)
  ADMIN_UNAUTHORIZED = 'ADMIN_UNAUTHORIZED',

  // Permission errors (mixed - see status code mapping for details)
  OWNER_REQUIRED = 'OWNER_REQUIRED', // 403 Forbidden
  ROLE_INSUFFICIENT = 'ROLE_INSUFFICIENT', // 401 Unauthorized (preserves original)
  SELF_ACTION_FORBIDDEN = 'SELF_ACTION_FORBIDDEN', // 401 Unauthorized (preserves original)

  // App Check errors (401 Unauthorized)
  APPCHECK_MISSING = 'APPCHECK_MISSING',
  APPCHECK_INVALID = 'APPCHECK_INVALID',

  // CSRF errors (401 Unauthorized - client retry logic depends on 401)
  CSRF_TOKEN_MISSING = 'CSRF_TOKEN_MISSING',
  CSRF_TOKEN_INVALID = 'CSRF_TOKEN_INVALID',
  CSRF_SECRET_NOT_FOUND = 'CSRF_SECRET_NOT_FOUND',
  CSRF_CHECK_FAILED = 'CSRF_CHECK_FAILED',

  // Validation errors (400 Bad Request)
  VALIDATION_INVALID_INPUT = 'VALIDATION_INVALID_INPUT',
  VALIDATION_MISSING_FIELD = 'VALIDATION_MISSING_FIELD',
  VALIDATION_INVALID_JSON = 'VALIDATION_INVALID_JSON',

  // Organization errors (404 Not Found)
  ORG_NOT_FOUND = 'ORG_NOT_FOUND',
  ORG_MEMBER_NOT_FOUND = 'ORG_MEMBER_NOT_FOUND',
  ORG_USER_NOT_FOUND = 'ORG_USER_NOT_FOUND',
  // Organization billing limits (409 Conflict)
  ORG_SEAT_LIMIT_REACHED = 'ORG_SEAT_LIMIT_REACHED',
  ORG_SEAT_CAPACITY_CHANGED = 'ORG_SEAT_CAPACITY_CHANGED',

  // Presentation errors
  PRESENTATION_NOT_FOUND = 'PRESENTATION_NOT_FOUND',
  PRESENTATION_ACCESS_DENIED = 'PRESENTATION_ACCESS_DENIED',
  // PRESENTATION_ID_INVALID: Used when presentation lookup fails in request validation
  // Mapped to 400 to preserve original throwBadRequestException behavior
  PRESENTATION_ID_INVALID = 'PRESENTATION_ID_INVALID',

  // Job errors (404 Not Found)
  JOB_NOT_FOUND = 'JOB_NOT_FOUND',

  // Studio errors (400 Bad Request)
  STUDIO_NOT_FOUND = 'STUDIO_NOT_FOUND',

  // Webhook errors
  WEBHOOK_INVALID_TOKEN = 'WEBHOOK_INVALID_TOKEN',
  WEBHOOK_INVALID_SIGNATURE = 'WEBHOOK_INVALID_SIGNATURE',
  WEBHOOK_MISSING_SIGNATURE = 'WEBHOOK_MISSING_SIGNATURE',
  WEBHOOK_INVALID_PAYLOAD = 'WEBHOOK_INVALID_PAYLOAD',

  // Stripe errors
  STRIPE_WEBHOOK_ERROR = 'STRIPE_WEBHOOK_ERROR',
  STRIPE_MISSING_SIGNATURE = 'STRIPE_MISSING_SIGNATURE',

  // Promo errors
  PROMO_INVALID_REQUEST = 'PROMO_INVALID_REQUEST',

  // AI errors
  AI_GENERATOR_NOT_FOUND = 'AI_GENERATOR_NOT_FOUND',
  AI_PROVIDER_CREDITS_EXHAUSTED = 'AI_PROVIDER_CREDITS_EXHAUSTED',
  AI_PROVIDER_RATE_LIMITED = 'AI_PROVIDER_RATE_LIMITED',
  AI_PROVIDER_AUTH_FORBIDDEN = 'AI_PROVIDER_AUTH_FORBIDDEN',
  AI_PROVIDER_INVALID_REQUEST = 'AI_PROVIDER_INVALID_REQUEST',
  AI_PROVIDER_UPSTREAM_UNAVAILABLE = 'AI_PROVIDER_UPSTREAM_UNAVAILABLE',
  AI_PROVIDER_CAPACITY_UNCERTAIN = 'AI_PROVIDER_CAPACITY_UNCERTAIN',
  AI_PROVIDER_TIMEOUT = 'AI_PROVIDER_TIMEOUT',
  AI_PROVIDER_EMPTY_RESPONSE = 'AI_PROVIDER_EMPTY_RESPONSE',
  AI_PROVIDER_UNKNOWN = 'AI_PROVIDER_UNKNOWN',

  // Payment errors (402 Payment Required)
  PAYMENT_REQUIRED = 'PAYMENT_REQUIRED',

  // Stripe portal errors
  STRIPE_PORTAL_INVALID_REQUEST = 'STRIPE_PORTAL_INVALID_REQUEST',
  STRIPE_PORTAL_NO_CUSTOMER = 'STRIPE_PORTAL_NO_CUSTOMER',

  // User account errors
  USER_RESET_DISABLED = 'USER_RESET_DISABLED',
  USER_NO_ORGANIZATION = 'USER_NO_ORGANIZATION',
  USER_CLEAR_ONBOARDED_DISABLED = 'USER_CLEAR_ONBOARDED_DISABLED',

  // Invite errors
  INVITE_NOT_FOUND = 'INVITE_NOT_FOUND',

  // Test endpoint errors (staging only)
  TEST_ENDPOINT_DISABLED = 'TEST_ENDPOINT_DISABLED',
  TEST_ENDPOINT_NOT_CONFIGURED = 'TEST_ENDPOINT_NOT_CONFIGURED',
  TEST_ENDPOINT_UNAUTHORIZED = 'TEST_ENDPOINT_UNAUTHORIZED',

  // Method errors (405 Method Not Allowed)
  METHOD_NOT_ALLOWED = 'METHOD_NOT_ALLOWED',

  // Onboarding errors (400 Bad Request - preserving original status)
  ONBOARDING_NO_ORGANIZATION = 'ONBOARDING_NO_ORGANIZATION',

  // Generic errors (use sparingly - prefer specific codes)
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}
